import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"

export default class extends Controller {
  static targets = ["input", "preview", "previewImage", "modal", "cropImage", "aspectInfo"]

  connect() {
    this.cropper = null
    this.currentAspect = NaN // free
  }

  disconnect() {
    this.destroyCropper()
  }

  // Called when file input changes
  fileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    if (!file.type.match(/^image\/(jpeg|png|webp|gif)$/)) return

    const url = URL.createObjectURL(file)
    this.originalFile = file
    this.openCropper(url)
  }

  openCropper(url) {
    this.cropImageTarget.src = url
    this.modalTarget.classList.add("image-cropper-modal--open")
    document.body.style.overflow = "hidden"

    // Wait for image to load before initializing cropper
    this.cropImageTarget.onload = () => {
      this.destroyCropper()
      this.cropper = new Cropper(this.cropImageTarget, {
        viewMode: 1,
        dragMode: "move",
        aspectRatio: NaN,
        autoCropArea: 0.9,
        responsive: true,
        restore: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        minContainerWidth: 200,
        minContainerHeight: 200,
      })
      this.currentAspect = NaN
      this.updateAspectInfo()
    }
  }

  setAspect(event) {
    const ratio = event.currentTarget.dataset.ratio
    if (ratio === "free") {
      this.currentAspect = NaN
    } else {
      const [w, h] = ratio.split(":").map(Number)
      this.currentAspect = w / h
    }
    if (this.cropper) {
      this.cropper.setAspectRatio(this.currentAspect)
    }
    this.updateAspectInfo()

    // Update active button
    this.element.querySelectorAll(".crop-aspect-btn").forEach(btn => {
      btn.classList.toggle("crop-aspect-btn--active", btn === event.currentTarget)
    })
  }

  updateAspectInfo() {
    if (!this.hasAspectInfoTarget) return
    if (isNaN(this.currentAspect)) {
      this.aspectInfoTarget.textContent = "Livre"
    } else if (this.currentAspect === 1) {
      this.aspectInfoTarget.textContent = "1:1"
    } else if (Math.abs(this.currentAspect - 4/3) < 0.01) {
      this.aspectInfoTarget.textContent = "4:3"
    } else {
      this.aspectInfoTarget.textContent = "16:9"
    }
  }

  zoomIn() {
    if (this.cropper) this.cropper.zoom(0.1)
  }

  zoomOut() {
    if (this.cropper) this.cropper.zoom(-0.1)
  }

  rotateLeft() {
    if (this.cropper) this.cropper.rotate(-90)
  }

  rotateRight() {
    if (this.cropper) this.cropper.rotate(90)
  }

  reset() {
    if (this.cropper) this.cropper.reset()
  }

  confirm() {
    if (!this.cropper) return

    const canvas = this.cropper.getCroppedCanvas({
      maxWidth: 1920,
      maxHeight: 1920,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high",
    })

    canvas.toBlob((blob) => {
      if (!blob) return

      // Create a new File from the blob
      const ext = this.originalFile.type === "image/png" ? "png" : "jpg"
      const file = new File([blob], `cropped.${ext}`, {
        type: this.originalFile.type === "image/png" ? "image/png" : "image/jpeg"
      })

      // Assign to file input using DataTransfer
      const dt = new DataTransfer()
      dt.items.add(file)
      this.inputTarget.files = dt.files

      // Show preview
      this.previewImageTarget.src = URL.createObjectURL(blob)
      this.previewTarget.classList.add("post-compose__preview--visible")

      this.closeModal()
    }, this.originalFile.type === "image/png" ? "image/png" : "image/jpeg", 0.92)
  }

  cancelCrop() {
    this.inputTarget.value = ""
    this.closeModal()
  }

  closeModal() {
    this.modalTarget.classList.remove("image-cropper-modal--open")
    document.body.style.overflow = ""
    this.destroyCropper()
  }

  removeImage() {
    this.inputTarget.value = ""
    this.previewTarget.classList.remove("post-compose__preview--visible")
    this.previewImageTarget.src = ""
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }
}
